import React, {useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {useTable} from 'react-table';
import {useDispatch} from 'react-redux';
import InfiniteScroll from 'react-infinite-scroll-component';
import {Form, Formik} from 'formik';
import * as columnFormatters from '../ColumnFormatters';
import * as commonColumnFormatters from '../../../_common/column-formatters';
import PopupSendToCreation from '../PopupSendToCreation';
import RadioSelectAll from '../RadioSelectAll';
import SortingColumnHeader from './SortingColumnHeader';
import useInfiniteScrollControl from '../../../_common/components/_custom-hooks/useInfiniteScrollControl';
import {useInvoiceOrdersUIContext} from '../InvoiceOrdersUIContext';
import * as actions from '../../_redux/invoiceOrdersActions';

export function InvoiceOrdersTableEditAndSend() {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [tableRows, setTableRows] = useState([]);
  const prefix = 'invoice-orders-column';
  const columns = useMemo(() => {
    return window.innerWidth < 990
      ? [
        {accessor: 'radio', Header: ''},
        {accessor: 'card', Header: ''},
        {accessor: 'actions', Header: ''}
      ]
      : [
        {accessor: 'radio', Header: ''},
        {
          accessor: 'createdAt',
          Header: <SortingColumnHeader header={`${prefix}-createdAt`} defaultMessage={'Created at'}
                                       fieldName={'createdAt'}/>
        },
        {accessor: 'agent', Header: intl.formatMessage({id: `${prefix}-agent`, defaultMessage: 'Agent'})},
        {
          accessor: 'customerName',
          Header: intl.formatMessage({id: `${prefix}-customerName`, defaultMessage: 'Customer name'})
        },
        {accessor: 'phone', Header: intl.formatMessage({id: `${prefix}-phone`, defaultMessage: 'Phone'})},
        {
          accessor: 'totalAmount',
          Header: <SortingColumnHeader header={`${prefix}-totalAmount`} defaultMessage={'Total amount'}
                                       fieldName={'total'}/>
        },
        {accessor: 'statusText', Header: intl.formatMessage({id: `${prefix}-status`, defaultMessage: 'Status text'})},
        {accessor: 'view', Header: intl.formatMessage({id: `${prefix}-view`, defaultMessage: 'View'})},
        {accessor: 'edit', Header: intl.formatMessage({id: `${prefix}-edit`, defaultMessage: 'Edit'})},
        {
          accessor: 'wasCreated',
          Header: intl.formatMessage({id: `${prefix}-wasCreated`, defaultMessage: 'Was created'})
        },
        {
          accessor: 'whatsappLink',
          Header: intl.formatMessage({id: `${prefix}-whatsappLink`, defaultMessage: 'WhatsApp'})
        },
      ];
  }, []);
  const data = useMemo(() => [
    ...tableRows.map((item) => {
      const rivhitStatusText = typeof item.rivhitStatusText === 'string' ? item.rivhitStatusText : '';
      return window.innerWidth < 990
        ? {
          radio: columnFormatters.RadioColumnFormatter(item),
          card: columnFormatters.CardConfirmColumnFormatter(item),
          actions: columnFormatters.MobileActionsColumnFormatter(item, intl, dispatch),
        }
        : {
          radio: columnFormatters.RadioColumnFormatter(item),
          createdAt: commonColumnFormatters.DateTimeColumnFormatter(item.createdAt),
          agent: columnFormatters.TwoLinesColumnFormatter(item),
          customerName: commonColumnFormatters.LimitContentWidthBoldColumnFormatter(item.customerName),
          phone: commonColumnFormatters.BoldWrapColumnFormatter(item.phone),
          totalAmount: commonColumnFormatters.LimitDecimalColumnFormatter(item.total),
          statusText: [
            commonColumnFormatters.BoldColumnFormatter(item.statusText),
            commonColumnFormatters.LimitContentWidthNormalColumnFormatter(rivhitStatusText),
          ],
          view: columnFormatters.ViewColumnFormatter(item),
          edit: columnFormatters.EditColumnFormatter(item),
          wasCreated: columnFormatters.CreatedColumnFormatter(item, intl, dispatch),
          whatsappLink: columnFormatters.WhatsAppLinkColumnFormatter(item, intl),
        };
    })
  ], [tableRows]);
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({columns, data});

  const infiniteScrollProps = useInfiniteScrollControl({
    currentStateStoreId: 'invoiceOrdersEditAndSend',
    UIContext: useInvoiceOrdersUIContext(),
    getNewEntities: actions.getInvoiceOrdersEditAndSend,
    getNextEntities: actions.getNextInvoiceOrdersEditAndSend,
    onEntitiesChange: entities => {
      setTableRows(entities);
    },
    dataLength: tableRows.length,
    scrollableTarget: 'scrollContainer',
    scrollThreshold: 0.9,
    loader: (
      <div className={'spinner-container'}>
        <span className={'spinner spinner-md spinner-primary'}/>
      </div>
    ),
    style: {
      overflow: 'inherit'
    },
  });

  const initialValues = Object.assign(
    {},
    ...tableRows.map(item => ({
      [`1-${item._id}`]: false,
    })),
    ...tableRows.map(item => ({
      [`8-${item._id}`]: false,
    })),
  );

  return (
    <div id="scrollContainer">
      <InfiniteScroll {...infiniteScrollProps}>
        <Formik
          initialValues={initialValues}
          onSubmit={(values, {resetForm}) => {
            resetForm();
          }}
          enableReinitialize={true}
        >
          {({values, handleSubmit, dirty}) => (
            <Form onSubmit={handleSubmit} className="form form-label-right">
              <table
                {...getTableProps()}
                className="table table-head-custom table-vertical-center infinite-scroll-table invoice-orders-table-common"
              >
                <thead className="sticky-header head custom-thead">
                <PopupSendToCreation
                  dirty={dirty}
                  values={values}
                  handleSubmit={handleSubmit}
                  tableRows={tableRows}
                />
                {headerGroups.map(headerGroup => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column, index) => (
                      <th
                        {...column.getHeaderProps()}
                        id="custom-brand-color-tableHead"
                      >
                        {index === 0 && (
                          <div className={'d-flex align-items-center'}>
                            <RadioSelectAll rows={tableRows} type="type-1" label="מס"/>
                            <div className="mx-2"/>
                            <RadioSelectAll rows={tableRows} type="type-8" label="עסקה"/>
                          </div>
                        )}
                        {index > 1 && column.render('Header')}
                      </th>
                    ))}
                  </tr>
                ))}
                </thead>
                <tbody {...getTableBodyProps()} className="body">
                {rows.map((row) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map(cell => {
                        return <td {...cell.getCellProps()}>{cell.render('Cell')}</td>;
                      })}
                    </tr>
                  );
                })}
                </tbody>
              </table>
            </Form>
          )}
        </Formik>
      </InfiniteScroll>
    </div>
  );
}
